import { Injectable, OnDestroy, effect, inject, signal } from '@angular/core';
import { PlatformService } from './platform.service';
import { StorageService } from './storage/storage.service';
import { UserAppearance } from '@features/authentication/models/authentication.model';

export type Theme = 'bright' | 'dark' | 'adaptive';

/**
 * Owns the light/dark switch for the whole app. Toggling the `dark` class on
 * <html> drives Angular Material (via `color-scheme`) and Tailwind's `dark:`
 * variant at the same time — see src/styles.scss.
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService implements OnDestroy {
  private static readonly STORAGE_KEY = 'appearance';

  private readonly platformService = inject(PlatformService);
  private readonly storageService = inject(StorageService);

  private systemThemeListener: ((event: MediaQueryListEvent) => void) | null = null;

  public readonly theme = signal<Theme>(this.getInitialTheme());
  public readonly effectiveTheme = signal<'light' | 'dark'>('light');

  constructor() {
    effect(() => this.applyTheme(this.theme()));
  }

  public ngOnDestroy(): void {
    this.detachSystemThemeListener();
  }

  public setTheme(theme: Theme): void {
    this.theme.set(theme);
    if (this.platformService.isBrowser()) {
      this.storageService.setItem(ThemeService.STORAGE_KEY, theme);
    }
  }

  /** Toggle light <-> dark; from `adaptive`, flip away from whatever is showing. */
  public toggleTheme(): void {
    const current = this.theme();
    if (current === 'adaptive') {
      this.setTheme(this.effectiveTheme() === 'light' ? 'dark' : 'bright');
      return;
    }
    this.setTheme(current === 'bright' ? 'dark' : 'bright');
  }

  /** Apply the appearance stored on the signed-in user's settings. */
  public syncFromUserSettings(appearance: UserAppearance | undefined | null): void {
    if (!appearance) {
      return;
    }
    const theme = ThemeService.toTheme(appearance);
    if (this.theme() !== theme) {
      this.setTheme(theme);
    }
  }

  public isDark(): boolean {
    return this.effectiveTheme() === 'dark';
  }

  private getInitialTheme(): Theme {
    if (!this.platformService.isBrowser()) {
      return 'bright';
    }
    const saved = this.storageService.getItem(ThemeService.STORAGE_KEY);
    if (saved === 'bright' || saved === 'dark' || saved === 'adaptive') {
      return saved;
    }
    return 'adaptive';
  }

  private applyTheme(theme: Theme): void {
    if (!this.platformService.isBrowser() || typeof document === 'undefined') {
      return;
    }

    this.detachSystemThemeListener();

    if (theme !== 'adaptive') {
      this.setEffective(theme === 'dark' ? 'dark' : 'light');
      return;
    }

    const query = window.matchMedia('(prefers-color-scheme: dark)');
    this.setEffective(query.matches ? 'dark' : 'light');

    this.systemThemeListener = event => this.setEffective(event.matches ? 'dark' : 'light');
    query.addEventListener('change', this.systemThemeListener);
  }

  private setEffective(mode: 'light' | 'dark'): void {
    this.effectiveTheme.set(mode);
    const html = document.documentElement;
    html.classList.toggle('dark', mode === 'dark');
    html.setAttribute('data-theme', mode);
  }

  private detachSystemThemeListener(): void {
    if (this.systemThemeListener && this.platformService.isBrowser()) {
      window
        .matchMedia('(prefers-color-scheme: dark)')
        .removeEventListener('change', this.systemThemeListener);
    }
    this.systemThemeListener = null;
  }

  private static toTheme(appearance: UserAppearance): Theme {
    switch (appearance) {
      case UserAppearance.bright:
        return 'bright';
      case UserAppearance.dark:
        return 'dark';
      default:
        return 'adaptive';
    }
  }
}
